import { useState, useMemo } from "react";
import { num, toM, ml, money, UNITS_BASIC, CalcLayout, FormCol, Card, Field, Sel, HowTo, Advanced, AreaTable, ResultCard, useCatalog, ProductSelect } from "./shared";

export default function Tapiceria({ onSave, s }) {
  const items = useCatalog("tapiceria");
  const [pid, setPid] = useState("");
  const prod = items.find((p) => p.id === pid);
  const [u, setU] = useState("cm");
  const [mueble, setMueble] = useState("Sofá");
  const [unidades, setUnidades] = useState("1");
  const [rolloAncho, setRolloAncho] = useState("1.40");
  const [tela, setTela] = useState("Lisa");
  const [rapport, setRapport] = useState("0");
  const [margen, setMargen] = useState("10");
  const [secciones, setSecciones] = useState([{ nombre: "Asiento", largo: "180", ancho: "60", cant: "2" }, { nombre: "Respaldo", largo: "180", ancho: "50", cant: "1" }]);
  const [cierreM, setCierreM] = useState("3");
  const [vivoM, setVivoM] = useState("12");
  const [botones, setBotones] = useState("0");
  const [precioTela, setPrecioTela] = useState("");
  const [precioConf, setPrecioConf] = useState("");
  const pr = prod ? prod.price : num(precioTela);

  const r = useMemo(() => {
    const uni = Math.max(1, Math.round(num(unidades)) || 1);
    const rolloM = num(rolloAncho) || 1.4;
    const f = 1 + num(margen) / 100;
    const rap = num(rapport) / 100;
    const patronAplica = tela !== "Lisa" && tela !== "Piel";
    let mlTela = 0;
    secciones.forEach((sec) => {
      const cant = Math.max(1, Math.round(num(sec.cant)) || 1) * uni;
      let W = toM(sec.largo, u) * f, H = toM(sec.ancho, u) * f;
      if (patronAplica && rap > 0) H = Math.ceil(H / rap) * rap;
      const perRow = Math.max(1, Math.floor(rolloM / Math.min(W, rolloM)));
      const filas = Math.ceil(cant / perRow);
      mlTela += filas * H;
    });
    const cierre = num(cierreM) * uni * 1.05;
    const vivo = num(vivoM) * uni * 1.1;
    const bot = Math.round(num(botones)) * uni;
    const cMat = mlTela * pr;
    const cConf = num(precioConf) * uni;
    const total = cMat + cConf;
    const w = [];
    if (patronAplica && rap > 0) w.push("El rapport aumentó el consumo para hacer coincidir el patrón.");
    if (tela === "Piel") w.push("Piel: se recomienda considerar un factor de aprovechamiento (~75%) por defectos y formas.");
    if (prod && prod.stock < mlTela) w.push(`Inventario insuficiente de "${prod.name}": stock ${prod.stock} m, requieres ${mlTela.toFixed(2)} m.`);
    const rows = [
      { section: "Proyecto" },
      { label: "Mueble", value: mueble }, { label: "Unidades", value: `${uni}` }, { label: "Tipo de tela", value: tela },
      { label: "Ancho del rollo", value: ml(rolloM) }, { label: "Margen de confección", value: `${num(margen)}%` },
      { section: "Tela (optimización de corte)" },
      { label: "Secciones", value: `${secciones.length}` },
      { label: "Metros lineales de tela", value: ml(mlTela), strong: true },
      { section: "Accesorios" },
      ...(cierre ? [{ label: "Cierre", value: ml(cierre) }] : []),
      ...(vivo ? [{ label: "Vivo / cordón", value: ml(vivo) }] : []),
      ...(bot ? [{ label: "Botones", value: `${bot}` }] : []),
      { label: "Grapas / adhesivo", value: "Según armado" }, { label: "Guata / relleno", value: "Según secciones" },
      { section: "Costos" },
      { label: "Tela", value: money(cMat) }, { label: "Confección / mano de obra", value: money(cConf) },
      { label: "Total estimado", value: money(total), strong: true },
    ];
    return { rows, warnings: w, total, inputs: { u, mueble, unidades } };
  }, [u, mueble, unidades, rolloAncho, tela, rapport, margen, secciones, cierreM, vivoM, botones, pr, precioConf, prod]);

  return (
    <CalcLayout>
      <FormCol>
        <HowTo title="¿Cómo calcular tapicería?"><p>Captura las secciones del mueble (asiento, respaldo, brazos…), el margen de confección y el ancho del rollo. El sistema estima los metros de tela optimizando el corte y agrega cierres, vivos, botones y relleno.</p></HowTo>
        <Card>
          <Sel label="Unidad" value={u} onChange={setU} opts={UNITS_BASIC} testid="tp-unidad" />
          <Sel label="Tipo de mueble" value={mueble} onChange={setMueble} opts={["Silla", "Sillón", "Sofá", "Cabecera", "Cojines", "Banco", "Puff", "Otro"]} testid="tp-mueble" />
          <Field label="Unidades iguales" value={unidades} onChange={setUnidades} step="1" min="1" testid="tp-unidades" />
          <Sel label="Tipo de tela" value={tela} onChange={setTela} opts={["Lisa", "Estampada", "Direccional", "Rayada", "Terciopelo", "Piel"]} testid="tp-tela" />
          <Field label="Ancho del rollo (m)" value={rolloAncho} onChange={setRolloAncho} step="0.01" suffix="m" testid="tp-rolloancho" />
          {tela !== "Lisa" && tela !== "Piel" && <Field label="Rapport (cm)" value={rapport} onChange={setRapport} step="1" suffix="cm" testid="tp-rapport" />}
        </Card>
        <ProductSelect items={items} value={pid} onChange={setPid} testid="tp-product" />
        <AreaTable title="Secciones del mueble (largo × alto)" rows={secciones} setRows={setSecciones} u={u} addLabel="+ Sección" defaultName="Sección" testid="tp-secciones" />
        <Advanced>
          <Field label="Margen de confección (%)" value={margen} onChange={setMargen} step="1" suffix="%" testid="tp-margen" />
          <Field label="Cierre por unidad (m)" value={cierreM} onChange={setCierreM} step="0.1" suffix="m" testid="tp-cierre" />
          <Field label="Vivo/cordón por unidad (m)" value={vivoM} onChange={setVivoM} step="0.1" suffix="m" testid="tp-vivo" />
          <Field label="Botones por unidad" value={botones} onChange={setBotones} step="1" testid="tp-botones" />
          <Field label="Precio tela ($/m)" value={prod ? prod.price : precioTela} onChange={setPrecioTela} suffix="$" testid="tp-precio" />
          <Field label="Confección ($/unidad)" value={precioConf} onChange={setPrecioConf} suffix="$" testid="tp-precio-conf" />
        </Advanced>
      </FormCol>
      <ResultCard title="Cotización de Tapicería — GOÛT" modality="tapiceria" rows={r.rows} warnings={r.warnings} total={r.total} inputs={r.inputs} onSave={onSave} />
    </CalcLayout>
  );
}
